import { normalizeApplicationUrlOrigin } from "./matchesAssignedApplicationOrigin";

type ApplicationEntry = {
  id?: unknown;
  name?: unknown;
  url?: unknown;
};

function collectAssignedApplicationIds(applications: unknown): string[] {
  if (!Array.isArray(applications)) return [];

  const ids: string[] = [];
  for (const entry of applications) {
    if (!entry || typeof entry !== "object") continue;
    const { id } = entry as ApplicationEntry;
    if (typeof id === "string" && id.trim()) {
      ids.push(id.trim());
    }
  }
  return ids;
}

/**
 * Resolves the current microfrontend's application ID.
 * Priority: REACT_APP_APPLICATION_ID → auth/me app matching this origin → name match → sole assignment.
 */
export function resolveApplicationId(
  authUser?: Record<string, unknown> | null,
): string {
  const fromEnv = (process.env.REACT_APP_APPLICATION_ID ?? "").trim();
  if (fromEnv) return fromEnv;

  const applications = authUser?.applications;
  if (!Array.isArray(applications) || applications.length === 0) return "";

  const currentOrigin =
    typeof window !== "undefined" ? window.location.origin.toLowerCase() : "";

  if (currentOrigin) {
    for (const entry of applications) {
      if (!entry || typeof entry !== "object") continue;
      const { id, url } = entry as ApplicationEntry;
      if (typeof id !== "string" || !id.trim()) continue;
      if (typeof url !== "string") continue;
      const appOrigin = normalizeApplicationUrlOrigin(url);
      if (appOrigin && appOrigin === currentOrigin) {
        return id.trim();
      }
    }
  }

  const appName = (process.env.REACT_APP_APPLICATION_NAME ?? "").trim().toLowerCase();
  if (appName) {
    for (const entry of applications) {
      if (!entry || typeof entry !== "object") continue;
      const { id, name } = entry as ApplicationEntry;
      if (typeof id !== "string" || !id.trim()) continue;
      if (typeof name !== "string") continue;
      const normalizedName = name.trim().toLowerCase();
      if (normalizedName === appName || normalizedName.includes(appName)) {
        return id.trim();
      }
    }
  }

  const assignedIds = collectAssignedApplicationIds(applications);
  if (assignedIds.length === 1) return assignedIds[0];

  return "";
}
